import type { SxProps, Theme } from '@mui/material';

export type MessageGroupPosition = 'single' | 'first' | 'middle' | 'last';

type MessageLike = {
  is_from_me?: boolean;
};

export function getMessageGroupPosition<T extends MessageLike>(
  messages: T[],
  index: number
): MessageGroupPosition {
  const current = messages[index];
  const prev = messages[index - 1];
  const next = messages[index + 1];
  const sameAsPrev = !!prev && !!prev.is_from_me === !!current.is_from_me;
  const sameAsNext = !!next && !!next.is_from_me === !!current.is_from_me;

  if (!sameAsPrev && !sameAsNext) return 'single';
  if (!sameAsPrev && sameAsNext) return 'first';
  if (sameAsPrev && sameAsNext) return 'middle';
  return 'last';
}

/**
 * Forme iMessage : coins arrondis + pointe en bas dessinée avec 2 pseudo-éléments
 * (la pointe reprend la couleur de la bulle, puis un masque de la couleur de fond la galbe).
 *
 * @param maskColor Couleur de fond du fil de discussion (pour masquer la pointe).
 */
export function getAppleMessageBubbleSx(
  outgoing: boolean,
  backgroundColor: string,
  groupPosition: MessageGroupPosition,
  maskColor: string
): SxProps<Theme> {
  const radius = '18px';
  const showTail = groupPosition === 'single' || groupPosition === 'last';

  const tailSx: SxProps<Theme> = showTail
    ? outgoing
      ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            zIndex: 0,
            bottom: 0,
            right: -8,
            width: 20,
            height: 20,
            bgcolor: backgroundColor,
            borderBottomLeftRadius: '16px',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: 1,
            bottom: 0,
            right: -20,
            width: 20,
            height: 20,
            bgcolor: maskColor,
            borderBottomLeftRadius: '12px',
          },
        }
      : {
          '&::before': {
            content: '""',
            position: 'absolute',
            zIndex: 0,
            bottom: 0,
            left: -8,
            width: 20,
            height: 20,
            bgcolor: backgroundColor,
            borderBottomRightRadius: '16px',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: 1,
            bottom: 0,
            left: -20,
            width: 20,
            height: 20,
            bgcolor: maskColor,
            borderBottomRightRadius: '12px',
          },
        }
    : {};

  return {
    position: 'relative',
    borderRadius: radius,
    bgcolor: backgroundColor,
    ...tailSx,
  };
}

export function getMessageGroupSpacing(groupPosition: MessageGroupPosition): number {
  return groupPosition === 'last' || groupPosition === 'single' ? 1.75 : 0.3;
}
